import React, { useContext, useEffect, useState } from 'react'
import { LanguageContext } from '../LanguageSwitcher/LanguageContext'
import { CATEGORIES, ALL_ON, ALL_OFF, getConsent, setConsent, useConsent } from '../../lib/consent'
import './CookiePreferences.scss'

/**
 * The cookie manager: one switch per category, plus the essential row that
 * cannot be turned off. Used inline on the policy page and inside the
 * banner's "Manage" view. `onSaved` lets the banner close itself.
 */
const CookiePreferences = ({ onSaved, compact = false }) => {
  const { translations: t } = useContext(LanguageContext)
  const saved = useConsent()
  const [draft, setDraft] = useState(() => getConsent() || { ...ALL_OFF })
  const [savedFlash, setSavedFlash] = useState(false)

  // Keep the switches in step if the choice changes elsewhere (banner vs page).
  useEffect(() => {
    if (saved) setDraft(saved)
  }, [saved])

  const rows = [
    { id: 'essential', locked: true },
    ...CATEGORIES.map((id) => ({ id, locked: false })),
  ]

  const toggle = (id) => setDraft((d) => ({ ...d, [id]: !d[id] }))

  const save = (value) => {
    const v = value || draft
    setConsent(v)
    setDraft(v)
    setSavedFlash(true)
    setTimeout(() => setSavedFlash(false), 2500)
    if (onSaved) onSaved(v)
  }

  return (
    <div className={`ckp ${compact ? 'ckp-compact' : ''}`}>
      <ul className='ckp-list'>
        {rows.map((row) => {
          const on = row.locked ? true : Boolean(draft[row.id])
          return (
            <li key={row.id} className='ckp-row'>
              <div className='ckp-row-text'>
                <span className='ckp-row-title'>
                  {t[`ckCat_${row.id}`]}
                  {row.locked && <em className='ckp-row-locked'>{t.ckAlwaysOn}</em>}
                </span>
                <span className='ckp-row-desc'>{t[`ckCatD_${row.id}`]}</span>
              </div>
              <button
                type='button'
                role='switch'
                aria-checked={on}
                aria-label={t[`ckCat_${row.id}`]}
                className={`ckp-switch ${on ? 'ckp-switch-on' : ''} ${row.locked ? 'ckp-switch-locked' : ''}`}
                disabled={row.locked}
                onClick={() => !row.locked && toggle(row.id)}
              >
                <span className='ckp-switch-knob' />
              </button>
            </li>
          )
        })}
      </ul>

      <div className='ckp-actions'>
        <button type='button' className='ckp-btn' onClick={() => save()}>{t.ckSave}</button>
        <button type='button' className='ckp-btn ckp-btn-ghost' onClick={() => save({ ...ALL_ON })}>{t.ckAccept}</button>
        <button type='button' className='ckp-btn ckp-btn-ghost' onClick={() => save({ ...ALL_OFF })}>{t.ckEssential}</button>
        <span className='ckp-status' aria-live='polite'>
          {savedFlash ? t.ckSaved : saved ? t.ckCurrentSaved : t.ckNoChoice}
        </span>
      </div>
    </div>
  )
}

export default CookiePreferences
